import React from "react";
import { Box, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { ABOUT_ORDER_SOP_IMAGES } from "./constants/aboutOrderSopImages";
import type { AboutOrderSopImageConfig, AboutOrderSopSection } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageContainSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";
import { PublicInfoImageLayout } from "./enums";
import { renderAboutOrderSopStepBody } from "./utils/aboutOrderSopStepBody";

const AboutOrderSopImage: React.FC<{
  image: AboutOrderSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();
  const imageSx =
    image.layout === PublicInfoImageLayout.CONTAIN
      ? userGuideSectionImageContainSx
      : userGuideSectionImageSx;

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={imageSx}
    />
  );
};

type OrderSopGuideSectionsProps = {
  sections: AboutOrderSopSection[];
  sectionTestIdPrefix?: string;
  imageTestIdPrefix?: string;
};

const OrderSopGuideSections: React.FC<OrderSopGuideSectionsProps> = ({
  sections,
  sectionTestIdPrefix = "about-order-sop-section",
  imageTestIdPrefix = "about-order-sop-image"
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <>
      {sections.map((section, sectionIndex) => (
        <Box
          key={section.id}
          component="section"
          sx={publicInfoGuideSectionSx}
          data-testid={`${sectionTestIdPrefix}-${section.id}`}
        >
          <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
            {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
          </Typography>
          {section.introKey != null ? (
            <Typography
              variant="body1"
              sx={{ ...publicInfoBodySx, mb: 2 }}
              data-testid={`${section.id}-intro`}
            >
              {t(section.introKey, section.introFallback ?? "")}
            </Typography>
          ) : null}
          <Box component="ol" sx={publicInfoOrderedListSx}>
            {section.steps.map((step, stepIndex) => {
              const image =
                step.imageId != null
                  ? ABOUT_ORDER_SOP_IMAGES[step.imageId]
                  : undefined;

              return (
                <Box key={`${section.id}-${stepIndex}`} component="li">
                  <Typography
                    component="span"
                    variant="body1"
                    sx={publicInfoBodySx}
                  >
                    {renderAboutOrderSopStepBody(
                      t(step.bodyKey, step.bodyFallback)
                    )}
                  </Typography>
                  {image ? (
                    <AboutOrderSopImage
                      image={image}
                      testId={`${imageTestIdPrefix}-${step.imageId}`}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
          {section.supplementalNotes?.map((note, noteIndex) => (
            <Typography
              key={`${section.id}-note-${noteIndex}`}
              variant="body1"
              sx={{ ...publicInfoBodySx, mt: 2 }}
              data-testid={`${section.id}-supplemental-${noteIndex}`}
            >
              {t(note.bodyKey, note.bodyFallback)}
            </Typography>
          ))}
        </Box>
      ))}
    </>
  );
};

export default OrderSopGuideSections;
